import type { ID } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { metaActions } from '@store/reducers/manager/meta'
import type { ProjectUser } from '@store/reducers/manager/users/types'
import type { ThreadsActions } from '@store/reducers/threads'
import { SocketActions } from '@store/socketSaga'
import { getId } from '@store/utils'
import { call, put, select } from 'redux-saga/effects'

import API from '@/api'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { authSelector } from '@/hooks/useAuth'
import type { AuthState } from '@/types'
import { InternalThreadsActions } from '../actions'
import type { CreatePayload, Thread } from '../types'

type CreateCb = (
  project: ID,
  threadId: ID,
  arg: CreatePayload
) => Promise<Thread>

export function* create({ payload }: ReturnType<typeof ThreadsActions.create>) {
  const projectId: ID = yield select(migrationProjectSelector.getProjectId)
  const auth: AuthState = yield select(authSelector)

  const users: ProjectUser[] = yield select(
    migrationProjectSelector.getProjectRecipients
  )

  sendGtmEvent(GtmTriggerName.CREATE_THREAD)

  const id = getId()

  const owner = auth.authenticated ? auth.id : null

  yield put(
    InternalThreadsActions.add({
      ...payload,
      owner,
      uuid: id,
      resolved: false,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      // the optimistic pin shows the guest their own name straight away, the
      // same one the server will send back; a member is named from `owner`
      author: payload.author ?? null,
    })
  )

  const response: Thread = yield call(
    API.Threads.create as CreateCb,
    projectId,
    id,
    payload
  )

  // todo: move this logic to the server
  const existentUser = auth.authenticated && users.some((u) => u.id === auth.id)
  if (!existentUser) {
    yield put(metaActions.addRecipient({ ...auth, pending: false }))

    yield put(
      SocketActions.emit({
        eventName: 'sync',
        payload: metaActions.addRecipient({ ...auth, pending: false }),
      })
    )
  }

  yield put(
    SocketActions.emit({
      eventName: 'comments',
      payload: InternalThreadsActions.add(response),
    })
  )
}
